/**
 * SRL-U View Components
 * EXACT copy from IntelligenceStudioPage.jsx lines 2702-3427
 */

import React, { useState } from 'react';

import {
  AlertCircle,
  BookOpen,
  ChevronRight,
  FileQuestion,
  Lightbulb,
  Loader2,
  Map,
  Network,
  Quote,
  RefreshCw,
  Users,
  Zap,
} from 'lucide-react';

// ==========================================
// SRL-U VIEW COMPONENTS
// ==========================================

// Role View Content - Natural Language Summary of Semantic Roles
const RoleViewContent = ({ roleData, isLoading, onExtract, onRefresh, notebook }) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
        <span className="mt-2 text-sm text-slate-500">Analyzing document roles...</span>
      </div>
    );
  }

  const sections = roleData?.sections || [];
  const domain = roleData?.domain || 'general';

  // Domain badge colors
  const domainColors = {
    legal: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    financial: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    medical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    insurance: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    marketing: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    general: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  };

  if (!sections.length) {
    return (
      <div className="p-6 text-center">
        <Users className="h-10 w-10 mx-auto text-slate-300 dark:text-neutral-600 mb-3" />
        <p className="text-sm text-slate-500 dark:text-neutral-400 mb-3">
          No semantic roles extracted yet
        </p>
        <p className="text-xs text-slate-400 dark:text-neutral-500 mb-4 max-w-[220px] mx-auto">
          Extract universal semantic roles (actors, actions, conditions, timing) from your
          documents.
        </p>
        <button
          onClick={onExtract}
          className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700"
        >
          🧠 Extract SRL Now
        </button>
        <p className="mt-2 text-[10px] text-slate-400">
          Uses AI to identify key parties, obligations, conditions
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 overflow-y-auto h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700 dark:text-neutral-300">
            📋 Role Analysis
          </span>
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${domainColors[domain] || domainColors.general}`}
          >
            {domain.charAt(0).toUpperCase() + domain.slice(1)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onExtract}
            className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded hover:bg-amber-200"
            title="Re-extract SRL"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Role Sections */}
      <div className="space-y-4">
        {sections.map((section, idx) => (
          <div
            key={idx}
            className="bg-white dark:bg-neutral-700/50 rounded-lg p-3 border border-slate-200 dark:border-neutral-600"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{section.icon || '📌'}</span>
              <h4 className="text-sm font-medium text-slate-700 dark:text-neutral-200">
                {section.title}
              </h4>
              <span className="text-xs text-slate-400 dark:text-neutral-500">
                ({section.items?.length || 0})
              </span>
            </div>
            <div className="space-y-2">
              {(section.items || []).slice(0, 5).map((item, itemIdx) => (
                <div key={itemIdx} className="flex items-start gap-2 text-sm">
                  <span className="text-amber-600 mt-0.5">•</span>
                  <div>
                    <span className="font-medium text-slate-700 dark:text-neutral-300">
                      {item.name}
                    </span>
                    {item.description && (
                      <span className="text-slate-500 dark:text-neutral-400">
                        {' → '}
                        {item.description}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {(section.items?.length || 0) > 5 && (
                <p className="text-xs text-slate-400 dark:text-neutral-500 italic">
                  + {section.items.length - 5} more...
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Scenarios View Content - AI-Powered What-If Analysis
const ScenariosViewContent = ({ scenariosData, isLoading, onGenerate, notebook }) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
        <span className="mt-2 text-sm text-slate-500">Generating scenarios...</span>
      </div>
    );
  }

  const scenarios = scenariosData?.scenarios || [];

  // Risk level colors
  const riskColors = {
    high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
    medium:
      'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
    low: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800',
  };

  if (!scenarios.length) {
    return (
      <div className="p-6 text-center">
        <Zap className="h-10 w-10 mx-auto text-slate-300 dark:text-neutral-600 mb-3" />
        <p className="text-sm text-slate-500 dark:text-neutral-400 mb-3">
          No scenarios generated yet
        </p>
        <p className="text-xs text-slate-400 dark:text-neutral-500 mb-4 max-w-[220px] mx-auto">
          Generate AI-powered "what-if" scenarios based on document analysis.
        </p>
        <button
          onClick={onGenerate}
          className="px-4 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700"
        >
          ⚡ Generate Scenarios
        </button>
        <p className="mt-2 text-[10px] text-slate-400">Requires SRL extraction first</p>
      </div>
    );
  }

  return (
    <div className="p-4 overflow-y-auto h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-700 dark:text-neutral-300">
            🎭 What-If Scenarios
          </span>
          <span className="text-xs text-slate-400">{scenarios.length} scenarios</span>
        </div>
        <button
          onClick={onGenerate}
          className="text-xs px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded hover:bg-orange-200"
        >
          ⚡ Regenerate
        </button>
      </div>

      {/* Scenarios List */}
      <div className="space-y-4">
        {scenarios.map((scenario, idx) => (
          <div
            key={idx}
            className={`rounded-lg p-4 border ${riskColors[scenario.risk_level] || riskColors.medium}`}
          >
            {/* Scenario Header */}
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-sm font-medium flex items-center gap-2">
                <span className="text-lg">
                  {scenario.risk_level === 'high'
                    ? '🔴'
                    : scenario.risk_level === 'low'
                      ? '🟢'
                      : '🟡'}
                </span>
                {scenario.title}
              </h4>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  scenario.risk_level === 'high'
                    ? 'bg-red-200 text-red-800'
                    : scenario.risk_level === 'low'
                      ? 'bg-green-200 text-green-800'
                      : 'bg-yellow-200 text-yellow-800'
                }`}
              >
                {scenario.risk_level} risk
              </span>
            </div>

            {/* Trigger */}
            <div className="mb-2">
              <span className="text-xs font-medium text-slate-600 dark:text-neutral-400">If:</span>
              <span className="text-xs text-slate-700 dark:text-neutral-300">
                {scenario.trigger}
              </span>
            </div>

            {/* Consequences */}
            <div className="mb-2">
              <span className="text-xs font-medium text-slate-600 dark:text-neutral-400">
                Then:
              </span>
              <ul className="mt-1 space-y-1">
                {(scenario.consequences || []).map((c, cIdx) => (
                  <li
                    key={cIdx}
                    className="text-xs text-slate-700 dark:text-neutral-300 flex items-start gap-1"
                  >
                    <span className="text-orange-500">→</span> {c}
                  </li>
                ))}
              </ul>
            </div>

            {/* Recommendation */}
            {scenario.recommendation && (
              <div className="mt-3 pt-2 border-t border-current/20">
                <span className="text-xs font-medium text-slate-600 dark:text-neutral-400">
                  💡 Recommendation:
                </span>
                <span className="text-xs text-slate-700 dark:text-neutral-300">
                  {scenario.recommendation}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Graph View Content - Enhanced with meaningful knowledge display
const GraphViewContent = ({ graphData, isLoading, onRefresh, onRebuild, notebook }) => {
  const [activeSection, setActiveSection] = useState('summary');
  const [expandedType, setExpandedType] = useState(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
        <span className="ml-2 text-sm text-slate-500">Loading graph...</span>
      </div>
    );
  }

  const nodes = Array.isArray(graphData?.nodes) ? graphData.nodes : [];
  const rawEdges = graphData?.edges || graphData?.relationships || [];
  const edges = Array.isArray(rawEdges) ? rawEdges : [];
  const source = graphData?.source || 'unknown';
  const stats = graphData?.stats || {};
  const clauses = Array.isArray(graphData?.clauses) ? graphData.clauses : [];

  // Group nodes by type for better organization
  const nodesByType = nodes.reduce((acc, node) => {
    const type = (node.type || 'other').toLowerCase();
    if (!acc[type]) acc[type] = [];
    acc[type].push(node);
    return acc;
  }, {});

  // Extract meaningful categories
  const parties = [
    ...(nodesByType.person || []),
    ...(nodesByType.organization || []),
    ...(nodesByType.party || []),
  ];
  const locations = nodesByType.location || nodesByType.address || [];
  const entities = nodesByType.entity || [];
  const documents = nodesByType.document || [];
  const clauseNodes = nodesByType.clause || [];

  // Show build button if graph is empty
  if (!nodes.length || nodes.length === 0) {
    const isNeo4jConnected = source === 'neo4j' || graphData?.available === true;

    return (
      <div className="p-6 text-center">
        <Network className="h-10 w-10 mx-auto text-slate-300 dark:text-neutral-600 mb-3" />
        <p className="text-sm text-slate-500 dark:text-neutral-400 mb-3">
          {isNeo4jConnected ? 'Graph is empty' : 'No knowledge graph yet'}
        </p>
        <button
          onClick={onRebuild}
          className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 mb-2"
        >
          🔨 Build Knowledge Graph
        </button>
        <p className="text-[10px] text-slate-400 mt-2">
          Extracts parties, obligations, clauses & relationships
        </p>
      </div>
    );
  }

  // Type icons and colors
  const typeConfig = {
    party: { icon: '👤', color: 'blue', label: 'Parties' },
    person: { icon: '👤', color: 'blue', label: 'People' },
    organization: { icon: '🏢', color: 'teal', label: 'Organizations' },
    location: { icon: '📍', color: 'rose', label: 'Locations' },
    address: { icon: '📍', color: 'rose', label: 'Addresses' },
    clause: { icon: '📜', color: 'purple', label: 'Clauses' },
    obligation: { icon: '⚖️', color: 'amber', label: 'Obligations' },
    right: { icon: '✅', color: 'green', label: 'Rights' },
    date: { icon: '📅', color: 'orange', label: 'Dates' },
    amount: { icon: '💰', color: 'emerald', label: 'Amounts' },
    entity: { icon: '🏷️', color: 'slate', label: 'Entities' },
    document: { icon: '📄', color: 'indigo', label: 'Documents' },
  };

  const getTypeStyle = type => {
    const config = typeConfig[type] || { icon: '•', color: 'slate', label: type };
    const colorClasses = {
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
      teal: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
      rose: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800',
      purple:
        'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
      amber:
        'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
      green:
        'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
      orange:
        'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
      emerald:
        'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
      slate:
        'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
      indigo:
        'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    };
    return { ...config, classes: colorClasses[config.color] || colorClasses.slate };
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 p-3 border-b border-slate-200 dark:border-neutral-700">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Network className="h-4 w-4 text-amber-600" />
            <span className="font-medium text-sm text-slate-900 dark:text-white">
              Knowledge Graph
            </span>
            {source === 'neo4j' && (
              <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-[10px]">
                Neo4j ✓
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onRebuild}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-neutral-700 rounded text-slate-500 dark:text-neutral-400"
              title="Rebuild graph"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex gap-2 text-[10px]">
          <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full">
            {nodes.length} entities
          </span>
          <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
            {edges.length} relationships
          </span>
          {clauseNodes.length > 0 && (
            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
              {clauseNodes.length} clauses
            </span>
          )}
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex-shrink-0 flex border-b border-slate-200 dark:border-neutral-700 text-xs">
        {['summary', 'entities', 'relationships', 'clauses'].map(section => (
          <button
            key={section}
            onClick={() => setActiveSection(section)}
            className={`flex-1 py-2 px-2 capitalize transition-colors ${
              activeSection === section
                ? 'text-amber-600 dark:text-amber-400 border-b-2 border-amber-500 font-medium'
                : 'text-slate-500 dark:text-neutral-400 hover:text-slate-700'
            }`}
          >
            {section}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3">
        {/* Summary Section */}
        {activeSection === 'summary' && (
          <div className="space-y-3">
            {/* Key Parties Card */}
            {parties.length > 0 && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h5 className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-1">
                  👤 Key Parties ({parties.length})
                </h5>
                <div className="flex flex-wrap gap-1">
                  {parties.slice(0, 8).map((p, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-white dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs border border-blue-200 dark:border-blue-700"
                    >
                      {p.label || p.name || p.id}
                    </span>
                  ))}
                  {parties.length > 8 && (
                    <span className="text-xs text-blue-500">+{parties.length - 8} more</span>
                  )}
                </div>
              </div>
            )}

            {/* Key Relationships Summary */}
            {edges.length > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <h5 className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-1">
                  🔗 Key Relationships
                </h5>
                <div className="space-y-1.5">
                  {edges.slice(0, 5).map((edge, i) => (
                    <div key={i} className="flex items-center gap-1 text-xs">
                      <span className="font-medium text-amber-700 dark:text-amber-300 truncate max-w-[80px]">
                        {edge.from || edge.source}
                      </span>
                      <span className="px-1.5 py-0.5 bg-amber-200 dark:bg-amber-800 text-amber-600 dark:text-amber-300 rounded text-[10px] whitespace-nowrap">
                        {edge.type || edge.label || 'relates to'}
                      </span>
                      <span className="text-amber-600 dark:text-amber-400 truncate max-w-[80px]">
                        {edge.to || edge.target}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Clauses Summary */}
            {clauseNodes.length > 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <h5 className="text-xs font-medium text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-1">
                  📜 Contract Clauses ({clauseNodes.length})
                </h5>
                <div className="space-y-1">
                  {clauseNodes.slice(0, 4).map((clause, i) => (
                    <div key={i} className="text-xs text-amber-700 dark:text-amber-300 truncate">
                      • {clause.label || clause.name || clause.text || `Clause ${i + 1}`}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Entity Type Breakdown */}
            <div className="p-3 bg-slate-50 dark:bg-neutral-800 rounded-lg border border-slate-200 dark:border-neutral-700">
              <h5 className="text-xs font-medium text-slate-700 dark:text-slate-200 mb-2">
                📊 Entity Breakdown
              </h5>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(nodesByType)
                  .slice(0, 6)
                  .map(([type, items]) => {
                    const style = getTypeStyle(type);
                    return (
                      <div key={type} className={`p-2 rounded border ${style.classes}`}>
                        <span className="text-lg">{style.icon}</span>
                        <span className="ml-1 text-xs font-medium">{items.length}</span>
                        <span className="ml-1 text-[10px] opacity-75">{style.label}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* Entities Section */}
        {activeSection === 'entities' && (
          <div className="space-y-2">
            {Object.entries(nodesByType).map(([type, items]) => {
              const style = getTypeStyle(type);
              const isExpanded = expandedType === type;
              return (
                <div
                  key={type}
                  className="border border-slate-200 dark:border-neutral-700 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedType(isExpanded ? null : type)}
                    className={`w-full p-2 flex items-center justify-between ${style.classes} hover:opacity-80`}
                  >
                    <span className="flex items-center gap-2 text-xs font-medium">
                      <span>{style.icon}</span>
                      <span>{style.label}</span>
                      <span className="opacity-60">({items.length})</span>
                    </span>
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {isExpanded && (
                    <div className="p-2 bg-white dark:bg-neutral-900 max-h-48 overflow-y-auto">
                      {items.map((item, i) => (
                        <div
                          key={i}
                          className="py-1.5 px-2 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-neutral-800 rounded"
                        >
                          {item.label || item.name || item.id}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Relationships Section */}
        {activeSection === 'relationships' && (
          <div className="space-y-2">
            {edges.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-4">No relationships extracted</p>
            ) : (
              edges.map((edge, i) => (
                <div
                  key={i}
                  className="p-2 bg-slate-50 dark:bg-neutral-800 rounded-lg border border-slate-200 dark:border-neutral-700"
                >
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium truncate max-w-[100px]">
                      {edge.from || edge.source}
                    </span>
                    <span className="flex-shrink-0">→</span>
                    <span className="px-2 py-0.5 bg-amber-200 dark:bg-amber-800 text-amber-700 dark:text-amber-300 rounded text-[10px] font-medium">
                      {(edge.type || edge.label || 'RELATED').toUpperCase()}
                    </span>
                    <span className="flex-shrink-0">→</span>
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded truncate max-w-[100px]">
                      {edge.to || edge.target}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Clauses Section */}
        {activeSection === 'clauses' && (
          <div className="space-y-2">
            {clauseNodes.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-4">No clauses extracted</p>
            ) : (
              clauseNodes.map((clause, i) => (
                <div
                  key={i}
                  className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
                >
                  <div className="text-xs text-amber-800 dark:text-amber-200 font-medium mb-1">
                    Clause {i + 1}
                  </div>
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    {clause.label ||
                      clause.name ||
                      clause.text ||
                      clause.content ||
                      'No content available'}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Timeline View Content
const TimelineViewContent = ({ timelineData, isLoading, onBuild, notebook }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  const events = timelineData?.rawEvents || timelineData?.events || [];

  if (!events.length) {
    return (
      <div className="p-6 text-center">
        <Map className="h-10 w-10 mx-auto text-slate-300 dark:text-neutral-600 mb-3" />
        <p className="text-sm text-slate-500 dark:text-neutral-400 mb-3">No timeline events yet</p>
        <button
          onClick={() => onBuild?.(notebook?.id)}
          className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700"
        >
          Build Timeline
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h4 className="font-medium text-slate-900 dark:text-white mb-4">
        Timeline ({events.length} events)
      </h4>

      <div className="relative pl-4 border-l-2 border-amber-200 dark:border-amber-800 space-y-4">
        {events.slice(0, 15).map((event, i) => (
          <div key={i} className="relative">
            <div className="absolute -left-[21px] w-4 h-4 rounded-full bg-amber-500 border-2 border-white dark:border-neutral-800" />
            <div className="bg-slate-100 dark:bg-neutral-700 rounded-lg p-3">
              <p className="text-xs text-amber-600 dark:text-amber-400 font-medium mb-1">
                {event.dateDisplay || event.date || 'Unknown date'}
              </p>
              <p className="text-sm text-slate-700 dark:text-neutral-300">{event.label}</p>
              {event.fileName && (
                <p className="text-xs text-slate-400 mt-1">Source: {event.fileName}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Tools Content
const ToolsContent = ({ notebook, onMapClauses }) => {
  const tools = [
    {
      icon: <BookOpen className="h-4 w-4" />,
      label: 'Study Guide',
      color: 'text-blue-500',
      action: 'study',
    },
    {
      icon: <FileQuestion className="h-4 w-4" />,
      label: 'Generate FAQ',
      color: 'text-green-500',
      action: 'faq',
    },
    {
      icon: <Quote className="h-4 w-4" />,
      label: 'Compare Clauses',
      color: 'text-amber-500',
      action: 'clauses',
    },
    {
      icon: <Lightbulb className="h-4 w-4" />,
      label: 'Key Insights',
      color: 'text-amber-500',
      action: 'insights',
    },
    {
      icon: <AlertCircle className="h-4 w-4" />,
      label: 'Find Conflicts',
      color: 'text-red-500',
      action: 'conflicts',
    },
  ];

  const handleAction = action => {
    if (action === 'clauses' && onMapClauses) {
      const fileIds = notebook?.files?.map(f => f.fileId || f.id) || [];
      onMapClauses(notebook?.id, fileIds);
    }
    // Other actions can be added here
  };

  return (
    <div className="p-3 space-y-1">
      {tools.map((tool, i) => (
        <button
          key={i}
          onClick={() => handleAction(tool.action)}
          className="w-full flex items-center gap-3 p-3 text-sm text-slate-600 dark:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
        >
          <span className={tool.color}>{tool.icon}</span>
          <span>{tool.label}</span>
        </button>
      ))}
    </div>
  );
};

export {
  RoleViewContent,
  ScenariosViewContent,
  GraphViewContent,
  TimelineViewContent,
  ToolsContent,
};
